import { useMutation, useQuery } from "@tanstack/react-query";
import { Card } from "primereact/card";
import { DataTablePFSEvent } from "primereact/datatable";
import { useCallback, useState } from "react";
import { DCSQueryClient } from "../../queryClient";
import { If } from "../../components/If/If";
import { useFilterQuery } from "../../hooks/useFilterQuery";
import { useToast } from "../../Providers/ToastProvider";
import { requests } from "../../services/http-common";
import { IFilter } from "../../types";
import { DeleteItemPopup } from "./components/DeleteItemPopup/DeleteItemPopup";
import { ItemList } from "./components/ItemList/ItemList";
import { SetItemPopup } from "./components/SetItemPopup/SetItemPopup";
import { TItem } from "./components/types";
import classes from "./tabs/tabs.module.scss";
import { useEffectWithoutMount } from "../../helpers/helperHooks";

const defaultFilters = {
  first: 0,
  rows: 10,
  page: 1,
  filter: "",
  filterByStatus: "",
  filterByCustomerCategory: "",
  sortOrder: -1,
};

export const ContractType = () => {
  const [filters, setFilters] = useState<IFilter>(defaultFilters as IFilter);
  const [item, setItem] = useState<TItem | null>(null);
  const [itemDialog, setItemDialog] = useState(false);
  const [deleteItemDialog, setDeleteItemDialog] = useState(false);

  const filterQuery = useFilterQuery(filters);
  const { show } = useToast();

  const { data, refetch } = useQuery({
    queryKey: ["contract-type-options"],
    queryFn: () => {
      return requests.get(`/contract-type?${filterQuery}`);
    },
  });

  const addItem = useMutation({
    mutationFn: ({ name, file }: { name: string; file: File }) => {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("file", file);
      return requests.post("/contract-type", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        transformRequest: (formData: any) => formData,
      });
    },
    onSuccess: () => {
      hideItemDialog();
      show({ detail: "Item added successfully !" });
      DCSQueryClient.invalidateQueries({
        queryKey: ["contract-type-options"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const editItemRequest = useMutation({
    mutationFn: ({ name, id, file }: TItem) => {
      return requests.put(`/contract-type/${id}`, { name, file });
    },
    onSuccess: () => {
      hideItemDialog();
      show({ detail: "Item updated successfully !" });
      DCSQueryClient.invalidateQueries({
        queryKey: ["contract-type-options"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const deleteItem = useMutation({
    mutationFn: ({ id }: TItem) => {
      return requests.delete(`/contract-type/${id}`);
    },
    onSuccess: () => {
      setDeleteItemDialog(false);
      DCSQueryClient.invalidateQueries({
        queryKey: ["contract-type-options"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const uploadSowTemplate = useMutation({
    mutationFn: ({
      contractTypeId,
      sowTemplateFile,
    }: {
      contractTypeId: string;
      sowTemplateFile: File;
    }) => {
      const formData = new FormData();
      formData.append("file", sowTemplateFile);
      return requests.post(
        `/contract-type/${contractTypeId}/sow-template`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          transformRequest: (formData: any) => formData,
        }
      );
    },
    onSuccess: () => {
      DCSQueryClient.invalidateQueries({
        queryKey: ["contract-type-options"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const handleSetItem = (item: {
    name: string;
    id: string | null;
    file: File;
    sowTemplateFile?: File | null;
  }) => {
    const { sowTemplateFile, ...rest } = item;

    if (rest.id && rest.id !== "contractType") {
      editItemRequest.mutate(rest as TItem, {
        onSuccess: () => {
          if (sowTemplateFile && rest.id) {
            uploadSowTemplate.mutate({
              contractTypeId: rest.id,
              sowTemplateFile,
            });
          }
        },
      });
      return;
    }

    addItem.mutate(rest, {
      onSuccess: (data: any) => {
        const contractTypeId = data?.id;
        if (sowTemplateFile && contractTypeId) {
          uploadSowTemplate.mutate({
            contractTypeId,
            sowTemplateFile,
          });
        }
      },
    });
  };

  const onPage = useCallback(
    (event: DataTablePFSEvent) => {
      setFilters((currVal: any) => ({
        ...currVal,
        first: event.first,
        rows: event.rows,
        page: event.page,
      }));
    },
    [setFilters]
  );

  useEffectWithoutMount(() => {
    refetch();
  }, [filters, refetch]);

  const editItem = (item: TItem) => {
    setItem(item);
    setItemDialog(true);
  };

  const confirmDeleteItem = (item: TItem) => {
    setItem(item);
    setDeleteItemDialog(true);
  };

  const hideDeleteItemDialog = () => {
    setDeleteItemDialog(false);
  };

  const hideItemDialog = () => {
    setItemDialog(false);
    setItem(null);
  };

  // const handleAddItem = () => {
  //   setItem({
  //     name: "",
  //     id: "contractType",
  //   });
  //   setItemDialog(true);
  // };

  const cardTitle = () => {
    return (
      <div className={classes.cardTitle}>
        <h4>Contract type</h4>
        {/* //TODO: temporary removed */}
        {/* <PrivateAccess 
          roles={{
            dcs_client: [UserRoles.CONTRACT_TYPE_CREATE],
          }}
        >
          <Button
            icon="pi pi-plus"
            className=""
            label="New"
            onClick={handleAddItem}
          />
        </PrivateAccess> */}
      </div>
    );
  };

  return (
    <div>
      <div className={classes.container}>
        <Card title={cardTitle}>
          <ItemList
            totalRecords={data?.total}
            records={data?.result}
            first={filters.first}
            rows={filters.rows}
            onPage={onPage}
            rowActionDelete={confirmDeleteItem}
            rowActionEdit={editItem}
          />
        </Card>
        <If condition={deleteItemDialog}>
          <DeleteItemPopup
            item={item}
            onHide={hideDeleteItemDialog}
            onConfirm={deleteItem.mutate}
            deleteRequestPending={deleteItem.isLoading}
          />
        </If>
        <If condition={itemDialog}>
          <SetItemPopup
            item={item}
            onHide={hideItemDialog}
            onSubmit={handleSetItem}
            showAirslateTemplate
            showSowTemplate
          />
        </If>
      </div>
    </div>
  );
};
